import React, { useState } from 'react';

interface RSVPCardProps {
    index: number;
    guestName: string;
    isNameEditable: boolean;
    onNameChange: (name: string) => void;
    onAttendanceChange: (attendance: 'Asistirá' | 'No asistirá') => void;
    onDietaryRestrictionChange: (restriction: string) => void;
    onDietaryDetailsChange: (details: string) => void;
    onAllergiesChange: (hasAllergies: boolean) => void;
    onAllergyDetailsChange: (details: string) => void;
    initialAttendance?: 'Asistirá' | 'No asistirá';
    initialDietaryRestriction?: string;
    initialDietaryDetails?: string;
    initialHasAllergies?: boolean;
    initialAllergyDetails?: string;
}

const RSVPCard: React.FC<RSVPCardProps> = ({
    index,
    guestName,
    isNameEditable,
    onNameChange,
    onAttendanceChange,
    onDietaryRestrictionChange,
    onDietaryDetailsChange,
    onAllergiesChange,
    onAllergyDetailsChange,
    initialAttendance = 'Asistirá',
    initialDietaryRestriction = '',
    initialDietaryDetails = '',
    initialHasAllergies = false,
    initialAllergyDetails = ''
}) => {
    // const [name, setName] = useState(guestName);
    const [attendance, setAttendance] = useState<'Asistirá' | 'No asistirá'>(initialAttendance);
    const [dietaryRestriction, setDietaryRestriction] = useState(initialDietaryRestriction);
    const [dietaryDetails, setDietaryDetails] = useState(initialDietaryDetails);
    const [hasAllergies, setHasAllergies] = useState(initialHasAllergies);
    const [allergyDetails, setAllergyDetails] = useState(initialAllergyDetails);

    const isDisabled = attendance === 'No asistirá';

    const handleNameChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newName = e.target.value;
        // setName(newName);
        onNameChange(newName);
    };

    const handleAttendanceChange = (newAttendance: 'Asistirá' | 'No asistirá') => {
        setAttendance(newAttendance);
        onAttendanceChange(newAttendance);
        
        // Reset other fields when selecting "No asistirá"
        if (newAttendance === 'No asistirá') {
            setDietaryRestriction('');
            setHasAllergies(false);
            setAllergyDetails('');
            onDietaryRestrictionChange('');
            onAllergiesChange(false);
            onAllergyDetailsChange('');
        }
    };

    const handleDietaryRestrictionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setDietaryRestriction(value);
        onDietaryRestrictionChange(value);
    };

    const handleDietaryDetailsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setDietaryDetails(value);
        onDietaryDetailsChange(value);
    };

    const handleAllergiesChange = (newHasAllergies: boolean) => {
        setHasAllergies(newHasAllergies);
        onAllergiesChange(newHasAllergies);
        
        // Clear allergy details if "No" is selected
        if (!newHasAllergies) {
            setAllergyDetails('');
            onAllergyDetailsChange('');
        }
    };

    const handleAllergyDetailsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setAllergyDetails(value);
        onAllergyDetailsChange(value);
    };

    const rButtonClass = `w-4 h-4 bg-white rounded-full appearance-none border-5 border-white
                            checked:border-[#5c1a27]
                            checked:bg-white
                            checked:focus:ring-0
                            checked:bg-[radial-gradient(circle,_#fff_0%,_#fff_50%,_transparent_50%,_transparent_100%)]
                            checked:bg-[size:8px_8px]
                            checked:bg-center
                            checked:bg-no-repeat `

    return (
        <div className="bg-[#f2ece7] rounded-[40px] shadow-lg overflow-hidden mb-4">
            {/* Name Header */}
            <div className="px-10 py-6">
                {isNameEditable ? (
                    <textarea
                        placeholder="Nombre del invitado"
                        className="w-full bg-white rounded-md px-4 py-2 resize-none"
                        value={guestName}
                        onChange={handleNameChange}
                        rows={1}
                    />
                ) : (
                    <h3 className="text-[18px] md:text-[23.5px] font-bold text-[#606060]">{guestName}</h3>
                )}
            </div>

            {/* Attendance Section */}
            <div className="bg-gradient-to-r from-[#f78c49] to-[#e27539] py-4">
                <div className="flex items-center justify-center space-x-8">
                    <label className="inline-flex items-center space-x-2 text-white cursor-pointer">
                        <input
                            type="radio"
                            name={`attendance-${index}`}
                            value="Asistirá"
                            checked={attendance === 'Asistirá'}
                            onChange={() => handleAttendanceChange('Asistirá')}
                            className={rButtonClass}
                        />
                        <span className="text-[12pt] font-medium">Asistirá</span>
                    </label>
                    <label className="inline-flex items-center space-x-2 text-white cursor-pointer">
                        <input
                            type="radio"
                            name={`attendance-${index}`}
                            value="No asistirá"
                            checked={attendance === 'No asistirá'}
                            onChange={() => handleAttendanceChange('No asistirá')}
                            className={rButtonClass}
                        />
                        <span className="text-[12pt] font-medium">No asistirá</span>
                    </label>
                </div>
            </div>

            {/* Form Fields */}
            <div className="px-10 space-y-4 py-6">
                {/* Dietary Restrictions */}
                <div className="flex flex-row flex-wrap items-center">
                    <label className="block text-[#606060] text-[12pt] font-light mr-4">
                        Restricción dietética:
                    </label>
                    <select
                        value={dietaryRestriction}
                        onChange={handleDietaryRestrictionChange}
                        disabled={isDisabled}
                        className={`flex-initial rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent text-[#606060] ${
                            isDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'
                        }`}
                    >
                        <option value=""></option>
                        <option value="No Aplica">No Aplica</option>
                        <option value="Sin Gluten">Sin Gluten</option>
                        <option value="Deslactosado">Deslactosado</option>
                        <option value="Otro">Otra</option>
                    </select>
                    {dietaryRestriction === 'Otro' && !isDisabled && (
                        <textarea
                            placeholder="Especificar..."
                            className="bg-white w-full mt-2 rounded-md px-4 py-2 resize-none"
                            onChange={handleDietaryDetailsChange}
                            value={dietaryDetails}
                            rows={2}
                        />
                    )}
                </div>

                {/* Allergies */}
                <div className="flex flex-row flex-wrap items-center">
                    <label className="block text-[#606060] text-[12pt] font-light mr-4">
                        Alergias:
                    </label>
                    <div className="flex items-center space-x-6">
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name={`allergies-${index}`}
                                value="yes"
                                checked={hasAllergies}
                                onChange={() => handleAllergiesChange(true)}
                                disabled={isDisabled}
                                className={rButtonClass + `${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            />
                            <span className={`${isDisabled ? 'text-gray-400' : 'text-[#606060]'}`}>Sí</span>
                        </label>
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name={`allergies-${index}`}
                                value="no"
                                checked={!hasAllergies}
                                onChange={() => handleAllergiesChange(false)}
                                disabled={isDisabled}
                                className={rButtonClass + `${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            />
                            <span className={`${isDisabled ? 'text-gray-400' : 'text-[#606060]'}`}>No</span>
                        </label>
                    </div>
                    {hasAllergies && !isDisabled && (
                        <textarea
                            placeholder="Especificar..."
                            value={allergyDetails}
                            onChange={handleAllergyDetailsChange}
                            className="bg-white w-full mt-2 rounded-md px-4 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                            rows={2}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default RSVPCard;